// Maps colour names (incl. Indian ethnic-wear vocabulary) to a display hex.
// Longest-key substring matching handles multi-word names like "rama blue".

const MAP = {
  // neutrals
  "off white": "#F2EEE4", "offwhite": "#F2EEE4", "white": "#FFFFFF", "ivory": "#F4F1E6",
  "cream": "#F3EAD3", "beige": "#E4D5BE", "black": "#1A1A1A", "grey": "#8A8A8A", "gray": "#8A8A8A",
  "charcoal": "#36454F", "silver": "#C7C7C7", "brown": "#8B5A2B", "tan": "#D2B48C", "khaki": "#C3B091",
  // reds / pinks
  "red": "#C62828", "maroon": "#7B1E23", "wine": "#722F37", "rust": "#B7410E", "brick": "#A63A2A",
  "rani": "#E3006D", "rani pink": "#E3006D", "magenta": "#C2185B", "fuchsia": "#E040A0",
  "pink": "#E75480", "baby pink": "#F4C2C2", "onion": "#E1A6B4", "onion pink": "#E1A6B4",
  "rose": "#C76B7E", "coral": "#FF6F5E", "peach": "#FFB07C", "salmon": "#F08080", "cherry": "#B02A3A",
  // oranges / yellows
  "orange": "#EF6C2E", "rust orange": "#C0492B", "mustard": "#D9A404", "yellow": "#F2C230",
  "gold": "#C9A227", "golden": "#C9A227", "lemon": "#E6D34A", "amber": "#D98324",
  // greens
  "green": "#2E9E5B", "rama": "#0E8A7D", "rama blue": "#0E8A7D", "rama green": "#0E8A7D",
  "bottle green": "#0B6E4F", "bottle": "#0B6E4F", "dark green": "#14532D", "olive": "#6B7A2E",
  "mint": "#9FD8B6", "pista": "#9CC46A", "pistachio": "#9CC46A", "sea green": "#2E8B6E",
  "emerald": "#0F7A57", "parrot": "#7CB342", "parrot green": "#7CB342", "teal": "#0C8B8B",
  "teal blue": "#0C7C86", "firozi": "#26C4C0", "turquoise": "#30C3B5",
  // blues / purples
  "blue": "#1E67C6", "navy": "#1B2A4A", "navy blue": "#1B2A4A", "royal": "#26418F",
  "royal blue": "#26418F", "sky": "#7EC8E3", "sky blue": "#7EC8E3", "powder blue": "#B0D6E8",
  "indigo": "#3F3D8A", "cobalt": "#2A52BE", "denim": "#3B5B84",
  "purple": "#7B2CBF", "violet": "#7A4FB0", "lavender": "#B39DDB", "mauve": "#C08BA8",
  "lilac": "#C8A2C8", "plum": "#7A3E65", "grape": "#5E2750",
};

const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

export const isMulti = (name) => /multi|rainbow|assorted|printed|floral/i.test(name || "");

export const colorToHex = (name) => {
  const n = norm(name);
  if (!n) return null;
  if (MAP[n]) return MAP[n];
  const keys = Object.keys(MAP).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (n === k || n.includes(k) || n.split(" ").includes(k)) return MAP[k];
  }
  return null;
};

// Returns a CSS background value (solid hex or gradient) for a swatch.
export const swatchBg = (name) => {
  if (isMulti(name)) {
    return "conic-gradient(#E3006D, #F2C230, #2E9E5B, #1E67C6, #7B2CBF, #E3006D)";
  }
  return colorToHex(name) || "#B8B2A7";
};

// Light colours need a stronger border to be visible on the bone background.
export const isLightColor = (name) => {
  const hex = colorToHex(name);
  if (!hex || hex.length !== 7) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 210;
};

// Broad colour families for the Shop filter (order = display order; specific first for matching).
export const COLOR_FAMILIES = [
  { key: "Red", hex: "#C62828", kw: ["red", "cherry", "brick", "tomato", "scarlet"] },
  { key: "Maroon", hex: "#7B1E23", kw: ["maroon", "wine", "rust", "burgundy", "mahogany"] },
  { key: "Pink", hex: "#E75480", kw: ["pink", "rani", "magenta", "fuchsia", "rose", "onion", "blush", "peach"] },
  { key: "Orange", hex: "#EF6C2E", kw: ["orange", "coral", "salmon", "tangerine", "apricot"] },
  { key: "Yellow", hex: "#F2C230", kw: ["yellow", "gold", "golden", "mustard", "lemon", "amber", "cream", "beige"] },
  { key: "Green", hex: "#2E9E5B", kw: ["green", "olive", "mint", "pista", "parrot", "emerald", "sea", "bottle", "mehendi"] },
  { key: "Teal", hex: "#0C8B8B", kw: ["teal", "rama", "firozi", "turquoise", "aqua", "cyan"] },
  { key: "Blue", hex: "#1E67C6", kw: ["blue", "navy", "royal", "sky", "indigo", "cobalt", "denim", "peacock"] },
  { key: "Purple", hex: "#7B2CBF", kw: ["purple", "violet", "lavender", "mauve", "lilac", "plum", "grape"] },
  { key: "Brown", hex: "#8B5A2B", kw: ["brown", "tan", "khaki", "coffee", "chocolate", "camel"] },
  { key: "Grey", hex: "#8A8A8A", kw: ["grey", "gray", "charcoal", "silver", "steel"] },
  { key: "Black", hex: "#1A1A1A", kw: ["black"] },
  { key: "White", hex: "#F2EEE4", kw: ["white", "ivory", "off white", "offwhite", "chalk"] },
];

export const colorFamily = (name) => {
  const n = norm(name);
  if (!n) return null;
  for (const f of COLOR_FAMILIES) {
    if (f.kw.some((k) => n === k || n.includes(k) || n.split(" ").includes(k))) return f.key;
  }
  return null;
};
